package com.swetdot.swetgram.ui.auth

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Checkbox
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.Dp
import com.swetdot.swetgram.ui.design.HeaderActionInset
import com.swetdot.swetgram.ui.design.ContentWidth
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIconButton
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetScreenTitle
import com.swetdot.swetgram.ui.design.SwetTextButton
import com.swetdot.swetgram.ui.design.SwetTextField
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.count
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.text.resolveOrNull
import com.swetdot.swetgram.ui.text.text
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.auth_banner_account_suspended
import swetgram.shared.generated.resources.auth_banner_code_rejected
import swetgram.shared.generated.resources.auth_banner_invalid_credentials
import swetgram.shared.generated.resources.auth_banner_offline
import swetgram.shared.generated.resources.auth_banner_rate_limited
import swetgram.shared.generated.resources.auth_banner_rate_limited_seconds
import swetgram.shared.generated.resources.auth_banner_unexpected
import swetgram.shared.generated.resources.auth_banner_upgrade_required
import swetgram.shared.generated.resources.field_error_code_incomplete
import swetgram.shared.generated.resources.field_error_code_rejected
import swetgram.shared.generated.resources.field_error_display_name_too_long
import swetgram.shared.generated.resources.field_error_email_invalid
import swetgram.shared.generated.resources.field_error_password_needs_letter_and_digit
import swetgram.shared.generated.resources.field_error_password_rejected
import swetgram.shared.generated.resources.field_error_password_too_long
import swetgram.shared.generated.resources.field_error_password_too_short
import swetgram.shared.generated.resources.field_error_passwords_do_not_match
import swetgram.shared.generated.resources.field_error_required
import swetgram.shared.generated.resources.field_error_username_bad_characters
import swetgram.shared.generated.resources.field_error_username_reserved
import swetgram.shared.generated.resources.field_error_username_too_long
import swetgram.shared.generated.resources.field_error_username_too_short

/**
 * Sign-in, registration and email verification (SDS §16).
 *
 * Every control is the design system's: [SwetTextField] for §11.2's 52dp field, and
 * [SwetPrimaryButton] / [SwetTextButton] for §11.1's button pair. The Material originals they
 * replace each disagreed with the spec in a way the theme cannot reach -- `OutlinedTextField`
 * enforces a 56dp minimum and `Button` sources a pill shape from its own token.
 *
 *
 * THESE FIVE SURFACES WERE THE LAST ONES THE RE-SKIN HAD NOT REACHED, and the way that happened is
 * worth writing down because nothing in the build could have said so. The monochrome pass walked
 * `ui/design`, `ui/chat`, `ui/chats`, `ui/discover`, `ui/groups`, `ui/profile` and `ui/moderation`.
 * `ui/auth` was in the audit's scope and came back clean -- no glyph call sites to migrate, no raw
 * palette literals -- because it had never drawn a glyph and had always read its colours through
 * tokens. So it INHERITED the new palette perfectly and adopted none of the new vocabulary, and the
 * first screen a new user ever sees was the one screen still built to the old shape:
 *
 *  - The canvas was Material's `background` #0A0A0A, from the root `Surface` in `SwetgramTheme`,
 *    while every re-skinned surface in the app paints `background.deep` #000000 on its own root.
 *    1.06:1 -- invisible in a screenshot, and the exact seam DECISIONS.md §25 and §26 were written
 *    about.
 *  - The title was a hand-rolled `Text` at `headlineMedium` 24sp. [SwetScreenTitle] did not exist
 *    when this file was written; it does now, all four tabs and Profile use it, and it is 28sp.
 *  - Six password fields, no reveal toggle, and `SwetIcons.Eye` / `SwetIcons.EyeOff` sitting in the
 *    icon set with zero call sites in the app -- authored for this screen and never wired to it.
 *
 * WHAT THIS FILE NOW SPEAKS. `background.deep` edge to edge; [SwetScreenTitle] on the canvas rather
 * than a bar with a divider under it; `Space.screenMargin` so the fields align with the title; a
 * [SwetIcons.ChevronLeft] on the steps that were pushed onto another one; and no card, plate or
 * divider anywhere -- spacing does the separating, which is the rule the chat list and Discover
 * already hold.
 *
 * Field errors are shown only AFTER a field has been touched or the form submitted -- flagging
 * an empty password as invalid before the user has typed anything is noise, not help.
 *
 * Fields carry a placeholder rather than a floating label, matching §11.2's placeholder tone
 * (`Email or username`, `Password`).
 *
 * TEXT FIELDS ARE `rememberSaveable`, NOT `remember`. `androidApp`'s manifest declares no
 * `configChanges`, so the Activity is fully recreated on a rotation and a plain `remember` drops
 * everything typed -- on the one set of screens in the app where retyping is most expensive,
 * because a password is invisible and a verification code has to be fetched from an email again.
 * `configChanges` would hide the symptom and still lose the field to process death; saving the
 * value is the fix. Strings need no custom saver on any Compose Multiplatform target.
 *
 * The two multi-field forms below ([RegisterScreen], [ResetPasswordScreen]) hold their state in a
 * data class and are NOT saved yet: that needs a `Saver` per form, which is a change of shape
 * rather than a one-word fix. Recorded as still owed.
 *
 *
 * THE COPY ON THESE FIVE SCREENS IS NOT LOCALISED, AND THAT IS OLDER AND BIGGER THAN THIS PASS.
 *
 * Localisation pass 1 moved this file's BANNERS and FIELD ERRORS into `composeResources` and stopped
 * there. Every other word a user reads here is still a Kotlin literal: four titles, nine
 * placeholders, eleven button labels and five explanatory sentences, plus the notice `AuthFlow`
 * hands to [SignInScreen]. A Russian phone shows English on every screen of the signed-out flow and
 * then switches to Russian the moment a session exists.
 *
 * Nothing in the build says so. `CopyLintTest` governs the words wherever they live and has rules
 * that read Kotlin literals precisely because copy used to live there; what it does not have is a
 * rule that a user-visible literal must not live there ANY MORE. So this file passes every lint
 * while being the least translated part of the app.
 *
 * The two strings this pass added -- the reveal toggle's "Show password" / "Hide password", and the
 * back chevron's "Back" -- are literals for the same reason and are stated here rather than left to
 * be discovered. Adding a `values/` key for them alone would have made three translated strings on a
 * screen holding thirty untranslated ones, and `everyEnglishKeyIsTranslatedUnlessItIsDeliberatelyNot`
 * would have failed until somebody wrote the Russian. "Back" is not new copy in any case: it is the
 * word `SwetTopAppBar` already declares as its own `backContentDescription` default. The fix for all
 * of this is one localisation pass over `ui/auth`, not three keys bolted on here.
 */

/**
 * The gap between two fields of the same form.
 *
 * 16dp rather than the 8dp that shipped, and NAMED rather than written five times. A `SwetTextField`
 * grows a supporting line when it is in error, so on a form where two neighbours are both wrong,
 * 8dp put one field's error message 8dp under it and 8dp over the next box -- the message read as
 * belonging to whichever field the eye reached first. The one number is here so that a form cannot
 * pick a different rhythm from the form beside it.
 */
private val FieldGap: Dp = Space.two

/**
 * The gap between the last field and the primary action.
 *
 * Bigger than [FieldGap] on purpose: it is the seam between what the user is filling in and what
 * submits it, and it is the only gap on these screens that separates two different KINDS of thing.
 * SDS §6.2's rhythm, and the same 32dp `SwetEmptyState` puts above its own action.
 */
private val FormActionGap: Dp = Space.four

@Composable
fun SignInScreen(
    state: AuthUiState,
    onSignIn: (identifier: String, password: String) -> Unit,
    onGoToRegister: () -> Unit,
    onForgotPassword: () -> Unit,
    modifier: Modifier = Modifier,
    /**
     * Seeds the identifier field. Used after a password reset, which ends signed OUT -- the
     * server revokes every session -- so the one thing we can still do for the user is not make
     * them retype the address they just proved they own.
     */
    initialIdentifier: String = "",
    /** A calm one-liner above the form. Currently only "your password was changed". */
    notice: String? = null,
) {
    // Keyed on the seed so arriving back here from a reset replaces the field rather than
    // keeping whatever composition-scoped blank was there before.
    var identifier by rememberSaveable(initialIdentifier) { mutableStateOf(initialIdentifier) }
    var password by rememberSaveable { mutableStateOf("") }
    var submitted by rememberSaveable { mutableStateOf(false) }

    AuthScaffold(title = "Welcome back", state = state, modifier = modifier) {
        notice?.let { AuthNote(it) }

        SwetTextField(
            value = identifier,
            onValueChange = { identifier = it },
            placeholder = "Email or username",
            // Autocapitalising a username is actively wrong: the contract's usernames are
            // lowercase, so the keyboard must not fight the user.
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.None,
                keyboardType = KeyboardType.Email,
                imeAction = ImeAction.Next,
            ),
            errorText = if (submitted && identifier.isBlank()) {
                fieldErrorText(FieldError.Required).resolveOrNull()
            } else {
                null
            },
        )
        Spacer(Modifier.height(FieldGap))

        PasswordField(
            value = password,
            onValueChange = { password = it },
            // On sign-in the server decides; applying the registration rules here would lock
            // out anyone whose existing password predates them.
            error = if (submitted && password.isEmpty()) FieldError.Required else null,
            imeAction = ImeAction.Done,
        )
        Spacer(Modifier.height(FormActionGap))

        SwetPrimaryButton(
            text = "Sign in",
            onClick = {
                submitted = true
                if (identifier.isNotBlank() && password.isNotEmpty()) onSignIn(identifier, password)
            },
            // The spinner replaces the label inside the button, so there is no second progress
            // indicator elsewhere on the screen competing to say the same thing.
            loading = state.busy,
        )

        // §11.1 names this button's own use case, and the app follows half of the sentence: "no
        // background, accent text. Forgot password, terms, learn more."
        //
        // THE QUOTATION IS KEPT AND THE COLOUR HALF OF IT IS NOT FOLLOWED, deliberately, because the
        // SDS is a reference this repository does not own and editing the quote to match what
        // shipped would erase the divergence rather than record it. The label is `text.primary`:
        // `swetTextButtonColor` carries the whole argument, and under this palette it is not even a
        // choice any more -- the accent is #FFFFFF, so "accent text" and `text.primary` are the same
        // pixels, and the accent is a FILL that only ever appears under something else.
        //
        // Placed directly under the primary action and above the sign-up link, because it belongs to
        // the password the user just failed to remember.
        SwetTextButton(
            text = "Forgot password?",
            onClick = onForgotPassword,
            enabled = !state.busy,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )

        SwetTextButton(
            text = "Create an account",
            onClick = onGoToRegister,
            enabled = !state.busy,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )
    }
}

/**
 * Registration. SDS §27.4 fixes both the fields and their order: display name, username, email,
 * password, confirm password.
 *
 * The confirmation is the one field on this screen the server never sees -- `RegisterRequest` has
 * no property for it and the contract is frozen. It is here because every other field can be
 * proofread and this one cannot: a mistyped password creates a real account nobody can sign into,
 * and the only way back is the recovery flow, which needs the address typed two boxes above and
 * possibly mistyped too. The rule and its wording come from [validateConfirmation], shared with
 * [ResetPasswordScreen].
 */
@Composable
fun RegisterScreen(
    state: AuthUiState,
    onRegister: (RegisterForm) -> Unit,
    onGoToSignIn: () -> Unit,
    modifier: Modifier = Modifier,
) {
    var form by remember { mutableStateOf(RegisterForm()) }
    var submitted by remember { mutableStateOf(false) }
    // Which fields the user has changed since the last submit. See [staleAwareError].
    var edited by remember { mutableStateOf(emptySet<String>()) }
    val local = remember(form) { validateRegister(form) }
    val uri = LocalUriHandler.current

    fun errorFor(field: String, localError: FieldError?): FieldError? =
        staleAwareError(state, field, edited) ?: localError.takeIf { submitted }

    AuthScaffold(
        title = "Create your account",
        state = state,
        modifier = modifier,
        // The same destination "I already have an account" reaches at the foot of the form. A back
        // control at the top-left is where a pushed screen is expected to have one, and a user who
        // opened registration to look at it should not have to read to the bottom to leave.
        onBack = onGoToSignIn,
    ) {
        SwetTextField(
            value = form.displayName,
            onValueChange = { form = form.copy(displayName = it); edited = edited + "display_name" },
            placeholder = "Display name",
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.Words,
                imeAction = ImeAction.Next,
            ),
            errorText = fieldErrorText(errorFor("display_name", local.displayName)).resolveOrNull(),
        )
        Spacer(Modifier.height(FieldGap))

        SwetTextField(
            value = form.username,
            onValueChange = { form = form.copy(username = it); edited = edited + "username" },
            placeholder = "Username",
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.None,
                imeAction = ImeAction.Next,
            ),
            errorText = fieldErrorText(errorFor("username", local.username)).resolveOrNull(),
        )
        Spacer(Modifier.height(FieldGap))

        SwetTextField(
            value = form.email,
            onValueChange = { form = form.copy(email = it); edited = edited + "email" },
            placeholder = "Email",
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.None,
                keyboardType = KeyboardType.Email,
                imeAction = ImeAction.Next,
            ),
            errorText = fieldErrorText(errorFor("email", local.email)).resolveOrNull(),
        )
        Spacer(Modifier.height(FieldGap))

        PasswordField(
            value = form.password,
            onValueChange = { form = form.copy(password = it); edited = edited + "password" },
            error = errorFor("password", local.password),
            // Next, not Done: there is a field after this one now, and a keyboard that offers to
            // submit from the middle of the form invites exactly the half-filled request the
            // confirmation exists to prevent.
            imeAction = ImeAction.Next,
        )
        Spacer(Modifier.height(FieldGap))

        PasswordField(
            value = form.confirmPassword,
            onValueChange = { form = form.copy(confirmPassword = it) },
            // Purely local, with no server verdict to defer to: this field is not sent. Shown
            // only after a submit, matching [ResetPasswordScreen] -- flagging a mismatch while
            // the user is still mid-word would mark the field red on almost every keystroke.
            error = local.confirmPassword.takeIf { submitted },
            imeAction = ImeAction.Done,
            placeholder = "Confirm password",
        )
        Spacer(Modifier.height(FieldGap))

        Row(verticalAlignment = Alignment.CenterVertically) {
            Checkbox(
                checked = form.ageConfirmed,
                onCheckedChange = { form = form.copy(ageConfirmed = it) },
                enabled = !state.busy,
            )
            Text("I confirm that I am at least 16 years old.")
        }
        if (submitted && local.ageRequired) {
            Text(
                "You must be at least 16 years old to use Swetgram.",
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.labelMedium,
            )
        }

        Row(verticalAlignment = Alignment.CenterVertically) {
            Checkbox(
                checked = form.acceptedLegal,
                onCheckedChange = { form = form.copy(acceptedLegal = it) },
                enabled = !state.busy,
            )
            Text("I accept the current Terms and Privacy Policy.")
        }
        if (submitted && local.legalRequired) {
            Text(
                "Please read and accept the legal documents.",
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.labelMedium,
            )
        }
        SwetTextButton("Terms", onClick = { uri.openUri("https://swetgram.com/terms") })
        SwetTextButton("Privacy", onClick = { uri.openUri("https://swetgram.com/privacy") })
        SwetTextButton("Community Guidelines", onClick = { uri.openUri("https://swetgram.com/community-guidelines") })
        Spacer(Modifier.height(FormActionGap))

        SwetPrimaryButton(
            text = "Create account",
            onClick = {
                submitted = true
                // Cleared only when a request actually goes out. Clearing it on every tap would
                // resurrect the previous response's field errors whenever the local rules block
                // the submit -- the user fixes the taken username, taps, is stopped by a short
                // password, and "That username is not available" reappears under a name the
                // server was never asked about.
                if (local.isValid) {
                    edited = emptySet()
                    onRegister(form)
                }
            },
            loading = state.busy,
        )

        SwetTextButton(
            text = "I already have an account",
            onClick = onGoToSignIn,
            enabled = !state.busy,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )
    }
}

/**
 * Email verification. The code arrives by email as `ABCD-EFGH`; the field accepts it with or
 * without the hyphen and in any case, because retyping exactly what an email showed is not a
 * test the user should have to pass.
 */
@Composable
fun VerifyEmailScreen(
    state: AuthUiState,
    email: String,
    onVerify: (code: String) -> Unit,
    onResend: () -> Unit,
    /** Escape hatch: without it a user who mistyped their address is stuck here for good. */
    onSignOut: () -> Unit,
    modifier: Modifier = Modifier,
) {
    // Saved across a rotation: the code lives in an email the user has to leave the app to read,
    // and it is single-use, so losing it to a screen turn is the most expensive field in the app.
    var code by rememberSaveable { mutableStateOf("") }
    var submitted by rememberSaveable { mutableStateOf(false) }
    val error = remember(code, submitted) { validateCode(code).takeIf { submitted } }

    // SDS §16's tone for this screen: "Check your email", calm and short.
    //
    // No back chevron. This screen is not pushed onto anything -- `App.kt` shows it because the
    // session is unverified, and the way off it is to verify, or to sign out and start again. A
    // chevron here would offer a step backwards that does not exist.
    AuthScaffold(
        title = "Check your email",
        state = state,
        modifier = modifier,
        mark = SwetIcons.Mail,
    ) {
        AuthNote(
            // A session stored before the address was persisted has none, and "sent a code
            // to ." reads like a bug. Name the address when we have it, stay vague when not.
            if (email.isBlank()) {
                "We sent you a code. Enter it below to finish setting up your account."
            } else {
                "We sent a code to $email. Enter it below to finish setting up your account."
            },
        )

        SwetTextField(
            value = code,
            onValueChange = { code = it.uppercase() },
            placeholder = "Verification code",
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.Characters,
                imeAction = ImeAction.Done,
            ),
            errorText = fieldErrorText(error).resolveOrNull(),
        )
        Spacer(Modifier.height(FormActionGap))

        SwetPrimaryButton(
            text = "Verify",
            onClick = {
                submitted = true
                if (validateCode(code) == null) onVerify(normalizeCode(code))
            },
            loading = state.busy,
        )

        SwetTextButton(
            text = "Send a new code",
            onClick = onResend,
            enabled = !state.busy,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )
        SwetTextButton(
            text = "Use a different account",
            onClick = onSignOut,
            enabled = !state.busy,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )
    }
}

/**
 * Password recovery, step one: which address (SPS §8.5, "Password reset uses email recovery").
 *
 * The copy is written for a screen that CANNOT know whether the address is registered, and must
 * not appear to. "Enter the email on your account" describes what to type; it does not promise an
 * outcome, so the wording is still true when the address belongs to nobody.
 */
@Composable
fun ForgotPasswordScreen(
    state: AuthUiState,
    onSubmit: (email: String) -> Unit,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    initialEmail: String = "",
) {
    var email by rememberSaveable(initialEmail) { mutableStateOf(initialEmail) }
    var submitted by rememberSaveable { mutableStateOf(false) }
    val local = remember(email) { validateEmail(email) }

    AuthScaffold(
        title = "Reset your password",
        state = state,
        modifier = modifier,
        // Pushed off sign-in, and the only branch of the app reachable by somebody who cannot get
        // in: the way back matters more here than anywhere else in the flow. Same destination as
        // "Back to sign in" at the foot of the form.
        onBack = onBack,
    ) {
        AuthNote("Enter the email address on your account and we will send you a code.")

        SwetTextField(
            value = email,
            onValueChange = { email = it },
            placeholder = "Email",
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.None,
                keyboardType = KeyboardType.Email,
                imeAction = ImeAction.Done,
            ),
            // Purely a shape check against the same rule registration uses. It says nothing
            // about whether an account exists -- the client has no way to know, and an endpoint
            // that could tell it is exactly what the contract declines to provide.
            errorText = fieldErrorText(state.fieldErrors["email"] ?: local.takeIf { submitted }).resolveOrNull(),
        )
        Spacer(Modifier.height(FormActionGap))

        SwetPrimaryButton(
            text = "Send code",
            onClick = {
                submitted = true
                if (local == null) onSubmit(email.trim())
            },
            loading = state.busy,
        )

        SwetTextButton(
            text = "Back to sign in",
            onClick = onBack,
            enabled = !state.busy,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )
    }
}

/**
 * Password recovery, step two: the code and the replacement password.
 *
 * The heading and the code field are deliberately the same as [VerifyEmailScreen]'s -- both codes
 * are 8 characters from the same alphabet, shown as `ABCD-EFGH` in the email, and a user who has
 * verified an address here already knows how this screen works.
 *
 * The note is the security-critical sentence in this whole feature. It reads "If <address> has an
 * account" and never "we sent a code to <address>", because the server answers `202` identically
 * for an address that exists and one that does not; a screen that asserted delivery would supply
 * the certainty the API withholds, and anyone could test a list of addresses against it.
 */
@Composable
fun ResetPasswordScreen(
    state: AuthUiState,
    email: String,
    onReset: (code: String, newPassword: String) -> Unit,
    onResend: () -> Unit,
    /** Wrong address typed on the previous screen; without this the flow is a dead end. */
    onUseAnotherEmail: () -> Unit,
    modifier: Modifier = Modifier,
) {
    var form by remember { mutableStateOf(ResetForm()) }
    var submitted by remember { mutableStateOf(false) }
    var edited by remember { mutableStateOf(emptySet<String>()) }
    val local = remember(form) { validateReset(form) }

    fun errorFor(field: String, localError: FieldError?): FieldError? =
        staleAwareError(state, field, edited) ?: localError.takeIf { submitted }

    AuthScaffold(
        title = "Check your email",
        state = state,
        modifier = modifier,
        // `restartRecovery` is literally the step before this one -- "back to the address step",
        // as its own comment in `AuthViewModel` puts it -- so the chevron is a back control here
        // and not a shortcut to somewhere else. Same destination as "Use a different email".
        onBack = onUseAnotherEmail,
        mark = SwetIcons.Mail,
    ) {
        AuthNote(
            if (email.isBlank()) {
                "If that address has an account, we sent it a code. Enter it below with your " +
                    "new password."
            } else {
                "If $email has an account, we sent it a code. Enter it below with your new " +
                    "password."
            },
        )

        SwetTextField(
            value = form.code,
            onValueChange = { form = form.copy(code = it.uppercase()); edited = edited + "token" },
            placeholder = "Reset code",
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.Characters,
                imeAction = ImeAction.Next,
            ),
            // `token` is the contract's field name for the code, so a server rejection lands
            // under this box rather than as a banner the user has to map back to a field.
            errorText = fieldErrorText(errorFor("token", local.code)).resolveOrNull(),
        )
        Spacer(Modifier.height(FieldGap))

        PasswordField(
            value = form.newPassword,
            onValueChange = { form = form.copy(newPassword = it); edited = edited + "new_password" },
            error = errorFor("new_password", local.newPassword),
            imeAction = ImeAction.Next,
            placeholder = "New password",
        )
        Spacer(Modifier.height(FieldGap))

        PasswordField(
            value = form.confirmPassword,
            onValueChange = { form = form.copy(confirmPassword = it) },
            // Purely local: the server never sees this field, so there is no server verdict
            // to prefer over the comparison.
            error = local.confirmPassword.takeIf { submitted },
            imeAction = ImeAction.Done,
            placeholder = "Confirm new password",
        )
        Spacer(Modifier.height(FormActionGap))

        SwetPrimaryButton(
            text = "Set new password",
            onClick = {
                submitted = true
                // The whole form is checked against `Constraints` BEFORE the round trip: the
                // code is single-use, so a request the client could have known would fail still
                // costs an attempt against the 15-minute limiter. (Probed: the server validates
                // `new_password` BEFORE it looks the code up, so a bad password costs a limiter
                // attempt but not the code itself.)
                if (local.isValid) {
                    // Only on a real submit -- see the same guard on the registration button.
                    edited = emptySet()
                    onReset(normalizeCode(form.code), form.newPassword)
                }
            },
            loading = state.busy,
        )

        SwetTextButton(
            text = "Send a new code",
            onClick = onResend,
            enabled = !state.busy,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )
        SwetTextButton(
            text = "Use a different email",
            onClick = onUseAnotherEmail,
            enabled = !state.busy,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )
    }
}

/**
 * The server's verdict on a field, but only while it still describes what is on screen.
 *
 * A server verdict outranks the local guess -- it knows about taken usernames and spent codes, and
 * the client cannot. But [AuthUiState.fieldErrors] is a judgment on the text that was SENT, and
 * nothing clears it while the user types: it survives until the next request starts. Without this
 * check, "That username is not available" sits under a username the server has never seen, and
 * "That code is not valid or has expired" sits under a freshly typed code -- the screen contradicts
 * itself, and on the reset screen it does so at the exact moment the user is trying to recover.
 *
 * Once a field has been edited the verdict is about a string that is no longer there, so it is
 * dropped and the local rules take over until the next submit puts a fresh verdict in its place.
 */
private fun staleAwareError(
    state: AuthUiState,
    field: String,
    edited: Set<String>,
): FieldError? = state.fieldErrors[field].takeIf { field !in edited }

/**
 * The shared frame: the canvas, an optional back chevron, the screen title, the failure banner,
 * then the form.
 *
 * THE CANVAS IS `background.deep` AND IT IS PAINTED HERE, which is the single largest thing this
 * pass changed. Nothing under `ui/auth` painted a background at all: the fill came from the root
 * `Surface` in `SwetgramTheme`, which is `MaterialTheme.colorScheme.background` -- `background`
 * #0A0A0A, not `background.deep` #000000. Every re-skinned surface in the app paints the deep
 * canvas on its own root (`ChatListScreen`, `DiscoverScreen`, `GroupsScreen`, and the frame in
 * `ui/Navigation.kt` for the tabs), so the auth flow was 11/255 lighter than the app it opens onto.
 * That is 1.06:1 and nobody would see it side by side -- it shows as the app appearing to change
 * shade the moment a sign-in succeeds.
 *
 * THE TITLE IS [SwetScreenTitle] NOW, and this replaces an argument that was correct when it was
 * written. The old heading was `headlineMedium` because §5.3's type scale calls 24sp "auth title",
 * and it reasoned against `titleLarge` 20sp -- the app-bar slot -- on the grounds that a screen with
 * no app bar should not wear the app bar's heading. Both halves of that still hold. What changed is
 * that the app grew a THIRD option it did not have: the mockups replaced the toolbar with a 28sp
 * title sitting directly on the canvas, and `SwetScreenTitle` is that shape, on all four tabs and on
 * Profile. A house that has one screen-title component should not have its first screen hand-rolling
 * a `Text` 4sp smaller, and §5.3's rung loses to that -- the SDS names the size, the mockups name
 * the shape, and only one of the two is what a user reads as "this is the same app".
 *
 * THE BACK CHEVRON IS OUTSIDE THE SCROLL, and outside the centring. The form is vertically centred
 * (`Arrangement.Center`), so a chevron inside that column would ride up and down with the number of
 * fields under it and land in the middle of the screen on the short ones. It belongs to the top-left
 * corner, where every other pushed screen in the app puts it -- see `SwetTopAppBar`'s own.
 *
 * @param onBack null on a step that was not pushed onto another one: sign-in is the root of the
 *   flow, and verification is not reached by a push but by having a session that is not verified
 *   yet, so neither draws one. The three that do pass the callback they already had, so the chevron
 *   adds an affordance and never a destination.
 * @param mark one 32dp glyph above the title, for a screen whose subject is a thing rather than a
 *   form. Only the two "check your email" surfaces use it.
 */
@Composable
private fun AuthScaffold(
    title: String,
    state: AuthUiState,
    modifier: Modifier = Modifier,
    onBack: (() -> Unit)? = null,
    mark: ImageVector? = null,
    content: @Composable ColumnScope.() -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    Column(
        modifier = modifier
            .fillMaxSize()
            // Painted BEFORE the insets, so the strip behind the status bar and the strips beside a
            // cutout are the canvas too -- the same order, and the same reason, as the app frame in
            // `ui/Navigation.kt`.
            .background(colors.backgroundDeep)
            // The activity runs edge to edge and the sign-in flow sits OUTSIDE the app frame
            // (see `App.kt`), so it is the only surface that has to inset itself at both ends --
            // `ui/Navigation.kt` applies the status bar once for every screen behind the gate.
            //
            // Chained rather than summed: these are `windowInsetsPadding` modifiers, so each one
            // consumes what it applied and the keyboard's inset absorbs the navigation bar's
            // instead of stacking on top of it.
            .statusBarsPadding()
            // Without imePadding the fields sit under the keyboard, and the scroll lets a short
            // screen still reach the button on a small device in landscape.
            .imePadding()
            .navigationBarsPadding(),
    ) {
        if (onBack != null) {
            SwetIconButton(
                // The label the design system already uses for this control -- `SwetTopAppBar`
                // declares the same word as its own default. See the file's note on copy.
                contentDescription = "Back",
                onClick = onBack,
                // Every other control on these screens goes inert while a request is in flight;
                // leaving this one live would let a tap abandon a submit that is already gone.
                enabled = !state.busy,
                // Not `screenMargin`: a 24dp glyph centred in a 44dp target needs the target's own
                // 10dp of slack taken back out, or the mark sits 26dp from the edge against the
                // title's 16dp. `SwetScreenTitle` derives its trailing inset the same way.
                modifier = Modifier.padding(start = HeaderActionInset),
            ) { tint ->
                SwetIcon(SwetIcons.ChevronLeft, tint, contentDescription = null)
            }
        }

        Column(
            modifier = Modifier
                .align(Alignment.CenterHorizontally)
                .widthIn(max = ContentWidth.form)
                .fillMaxWidth()
                .weight(1f)
                .verticalScroll(rememberScrollState())
                // Inside the scroll, so it is air at both ends of the content rather than a frame
                // around the viewport: on a form long enough to scroll, the last text button stops
                // short of the bottom edge instead of touching it.
                .padding(vertical = Space.two),
            horizontalAlignment = Alignment.Start,
            verticalArrangement = Arrangement.Center,
        ) {
            if (mark != null) {
                SwetIcon(
                    icon = mark,
                    tint = colors.textPrimary,
                    // Decorative: the title under it says the same thing in words, and a screen
                    // reader that announced both would say it twice.
                    contentDescription = null,
                    size = Sizes.iconLarge,
                    modifier = Modifier.padding(start = Space.screenMargin, bottom = Space.two),
                )
            }

            // TWO LINES, not the component's default one. Every existing caller passes a single
            // word that cannot wrap; these five pass a sentence. At a large system font
            // "Create your account" on one line ellipsizes into something that no longer names the
            // screen, and this title has no action beside it and sits in a column that scrolls, so
            // a second line costs nothing that a truncated heading does not cost more of.
            SwetScreenTitle(title = title, maxLines = 2)
            Spacer(Modifier.height(Space.two))

            Column(Modifier.padding(horizontal = Space.screenMargin)) {
                state.banner?.let { banner ->
                    // The same banner the chat screens use, so a failure looks the same everywhere
                    // in the app rather than being red text here and a tinted bar there.
                    SwetErrorBanner(message = bannerText(banner).resolve())
                    Spacer(Modifier.height(Space.two))
                }

                content()
            }
        }
    }
}

/**
 * The explanatory line between an auth heading and its form.
 *
 * `body.medium` in `text.secondary`, per §5.3 -- NOT `text.tertiary`, which the design system
 * reserves for placeholder and disabled text. This is neither: it is the sentence that tells the
 * user what the screen is for, and it stays true while every field on it is filled.
 */
@Composable
private fun AuthNote(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = SwetgramThemeTokens.colors.textSecondary,
    )
    Spacer(Modifier.height(Space.two))
}

/**
 * SDS §18.2: say what happened and what to do next, never the raw error.
 *
 * Returns the message's IDENTITY rather than its words. `stringResource` is a `@Composable` and
 * this is not one, deliberately: which banner a failure becomes is a rule worth testing without a
 * renderer, and `AuthBannerTest` now asserts the key instead of the English. See [UiText].
 */
internal fun bannerText(banner: AuthBanner): UiText = when (banner) {
    AuthBanner.InvalidCredentials -> Res.string.auth_banner_invalid_credentials.text()
    AuthBanner.CodeRejected -> Res.string.auth_banner_code_rejected.text()
    // Says the thing, says it is not a mistake they can correct here, and points somewhere real.
    // No apology and no argument: this screen is not where a suspension gets discussed.
    AuthBanner.AccountSuspended -> Res.string.auth_banner_account_suspended.text()
    AuthBanner.UpgradeRequired -> Res.string.auth_banner_upgrade_required.text()
    AuthBanner.Offline -> Res.string.auth_banner_offline.text()
    // The figure printed is the server's own Long. The plural CATEGORY needs an Int, and a
    // `Retry-After` past Int.MAX_VALUE would wrap to a negative and pick a form at random, so the
    // count is clamped while the number on screen is not touched.
    is AuthBanner.RateLimited -> banner.retryAfterSeconds
        ?.let {
            Res.plurals.auth_banner_rate_limited_seconds
                .count(it.coerceIn(0L, Int.MAX_VALUE.toLong()).toInt(), it)
        }
        ?: Res.string.auth_banner_rate_limited.text()
    AuthBanner.Unexpected -> Res.string.auth_banner_unexpected.text()
}

/**
 * The helper text under a field, or null when the field is fine.
 *
 * A plain function rather than a composable: [SwetTextField] owns the danger colour and the
 * `label.medium` style of §11.2's error state, so all this has to decide is the words.
 */
internal fun fieldErrorText(error: FieldError?): UiText? = when (error) {
    null -> null
    FieldError.Required -> Res.string.field_error_required.text()
    FieldError.UsernameTooShort -> Res.string.field_error_username_too_short.text()
    FieldError.UsernameTooLong -> Res.string.field_error_username_too_long.text()
    FieldError.UsernameBadCharacters -> Res.string.field_error_username_bad_characters.text()
    FieldError.UsernameReserved -> Res.string.field_error_username_reserved.text()
    FieldError.EmailInvalid -> Res.string.field_error_email_invalid.text()
    FieldError.PasswordTooShort -> Res.string.field_error_password_too_short.text()
    FieldError.PasswordTooLong -> Res.string.field_error_password_too_long.text()
    FieldError.PasswordNeedsLetterAndDigit ->
        Res.string.field_error_password_needs_letter_and_digit.text()
    FieldError.PasswordsDoNotMatch -> Res.string.field_error_passwords_do_not_match.text()
    // Every rule at once, because a server rejection does not say which one broke -- and it is
    // still the rule rather than "invalid", so there is something to act on.
    FieldError.PasswordRejected -> Res.string.field_error_password_rejected.text()
    FieldError.DisplayNameTooLong -> Res.string.field_error_display_name_too_long.text()
    FieldError.CodeIncomplete -> Res.string.field_error_code_incomplete.text()
    FieldError.CodeRejected -> Res.string.field_error_code_rejected.text()
}

/**
 * A password box, with the reveal toggle all six of them were missing.
 *
 * WHY IT IS WORTH THE RISK IT LOOKS LIKE. A password field is the one input in the app whose
 * contents cannot be proofread, and this flow has six of them: two of those six exist ONLY to catch
 * a typo the user cannot see (`Confirm password`, `Confirm new password`), which is an admission
 * that the box is unreadable rather than a fix for it. The registration rules are 10 to 128
 * characters with at least one letter and one digit, so what is being typed blind on a phone
 * keyboard is long and mixed-case by construction. `SwetIcons.Eye` and `SwetIcons.EyeOff` were
 * authored for exactly this and had ZERO call sites in the app until now.
 *
 * THE STATE IS CARRIED BY THE GLYPH FIRST (SDS §22.2): `Eye` offers to show, `EyeOff` offers to
 * hide, so the control says which way it goes without any colour at all. The tint reinforces it and
 * never carries it -- revealed is `text.primary`, hidden is `text.secondary` -- which is the same
 * rule `swetTextButtonColor` states for the destructive label.
 *
 * THE DESCRIPTION CHANGES WITH THE STATE, and that is not decoration: a toggle whose label never
 * moves tells a screen-reader user what the button is and never what it just did. Both strings are
 * Kotlin literals like every other visible word on these five screens -- see the note on copy at the
 * top of this file.
 *
 * `KeyboardType.Password` is kept while revealed. It is what suppresses the keyboard's own
 * suggestion strip and personalised learning, and none of that becomes desirable because the text is
 * on screen for a moment; the only thing the reveal changes is the [VisualTransformation].
 *
 * `rememberSaveable`, so a rotation does not re-mask a field the user deliberately opened -- the
 * same argument the file's header makes for the values themselves, and the state it saves is one
 * Boolean about a field whose contents are already saved beside it.
 */
@Composable
private fun PasswordField(
    value: String,
    onValueChange: (String) -> Unit,
    error: FieldError?,
    imeAction: ImeAction,
    placeholder: String = "Password",
) {
    val colors = SwetgramThemeTokens.colors
    var revealed by rememberSaveable { mutableStateOf(false) }

    SwetTextField(
        value = value,
        onValueChange = onValueChange,
        placeholder = placeholder,
        visualTransformation = if (revealed) {
            VisualTransformation.None
        } else {
            PasswordVisualTransformation()
        },
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.None,
            keyboardType = KeyboardType.Password,
            imeAction = imeAction,
        ),
        errorText = fieldErrorText(error).resolveOrNull(),
        trailingIcon = {
            SwetIconButton(
                contentDescription = if (revealed) "Hide password" else "Show password",
                onClick = { revealed = !revealed },
                tint = if (revealed) colors.textPrimary else colors.textSecondary,
            ) { tint ->
                SwetIcon(
                    icon = if (revealed) SwetIcons.EyeOff else SwetIcons.Eye,
                    tint = tint,
                    // The button carries the description; a second one here would have the node
                    // announced twice.
                    contentDescription = null,
                    // §11.2's field is 52dp and the 44dp target already fills most of it. `icon.sm`
                    // is the SDS's "secondary actions" rung, which is what a reveal toggle is.
                    size = Sizes.iconSmallMedium,
                )
            }
        },
    )
}
